import { useCallback, useEffect, useMemo, useState } from 'react';
import { getNotifications, getUnreadCount } from '../services/notifications';

export const NOTIFICATION_STATE_EVENT = 'stratel:notification-state-changed';

export function publishNotificationState(detail = { kind: 'refresh' }) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_STATE_EVENT, { detail }));
  }
}

const ROUTE_ALIASES = {
  admin: [
    ['/training-management', '/lms/management'],
    ['/learning', '/lms/courses'],
    ['/billing', '/billing/financial-dashboard'],
    ['/staffing', '/staffing/requests'],
  ],
  client: [
    ['/billing/recurring', '/invoices'],
    ['/learning/courses', '/training'],
    ['/learning', '/training'],
  ],
  worker: [
    ['/calendar', '/learning'],
    ['/learning/courses', '/learning'],
    ['/shifts', '/shifts/my'],
  ],
};

const TYPE_ROUTES = {
  admin: [
    [/message|mention/, '/messages'], [/intake/, '/intake'], [/lead/, '/leads'],
    [/grant/, '/grants'], [/regulatory/, '/regulatory'], [/survey|campaign/, '/consulting/surveys'],
    [/document|comment/, '/documents'], [/signature/, '/signatures'], [/proposal|agreement/, '/proposals'],
    [/invoice|billing|payment|finance/, '/invoices'], [/timesheet/, '/staffing/timesheets'],
    [/shift|staffing|placement|wage|rate/, '/staffing/requests'], [/filing|payroll/, '/staffing/filings'],
    [/course|training|enrollment|certificate|deadline|learning/, '/lms/management'],
    [/compliance|retention/, '/compliance'], [/sla/, '/sla'],
  ],
  client: [
    [/message|mention/, '/messages'], [/document|comment/, '/documents'], [/signature/, '/signatures'],
    [/invoice|billing|payment/, '/invoices'], [/timesheet/, '/staffing/timesheet-approvals'],
    [/wage|rate/, '/staffing/rate-approvals'], [/staffing|placement|shift/, '/staffing/requests'],
    [/course|training|enrollment|certificate|deadline|learning/, '/training-management'],
  ],
  worker: [
    [/message|mention/, '/messages'], [/timesheet/, '/timesheets'], [/pay|invoice|payment/, '/pay-stubs'],
    [/credential|background/, '/credentials'], [/certificate/, '/learning/certificates'],
    [/course|training|enrollment|deadline|learning/, '/learning'], [/shift|staffing|placement/, '/shifts/my'],
  ],
};

function actionPath(actionUrl) {
  if (!actionUrl) return '';
  try {
    return new URL(actionUrl, 'https://stratel.local').pathname;
  } catch {
    return String(actionUrl).split(/[?#]/)[0];
  }
}

function targetFor(notification, portal, hrefs) {
  const path = actionPath(notification?.actionUrl);
  const aliases = ROUTE_ALIASES[portal] || [];
  const alias = aliases.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
  const aliasedPath = alias?.[1] || path;
  const direct = [...hrefs]
    .sort((a, b) => b.length - a.length)
    .find((href) => aliasedPath === href || aliasedPath.startsWith(`${href}/`));
  if (direct) return direct;

  const searchText = `${notification?.type || ''} ${notification?.relatedEntity?.type || ''}`.toLowerCase();
  const inferred = (TYPE_ROUTES[portal] || []).find(([pattern]) => pattern.test(searchText))?.[1];
  return hrefs.includes(inferred) ? inferred : '';
}

function hasPendingAction(notification) {
  const metadata = notification?.metadata || {};
  const action = notification?.actionRequired || metadata.actionRequired;
  const required = action === true || action?.required === true || metadata.requiresAction === true;
  const status = String(action?.status || metadata.actionStatus || '').toLowerCase();
  return required && !['resolved', 'completed', 'approved', 'denied', 'cancelled'].includes(status)
    && !action?.resolvedAt && !metadata.actionResolvedAt && !metadata.resolvedAt;
}

export function useNavigationNotifications(token, portal, availableHrefs = []) {
  const [notifications, setNotifications] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const hrefKey = availableHrefs.join('|');

  const refresh = useCallback(async () => {
    if (!token) {
      setNotifications([]);
      setTotalUnread(0);
      return;
    }
    try {
      const [data, countData] = await Promise.all([getNotifications(token, { limit: 200 }), getUnreadCount(token)]);
      setNotifications(data?.notifications || []);
      setTotalUnread(Math.max(0, countData?.count ?? data?.unreadCount ?? 0));
    } catch (error) {
      console.error('Failed to load navigation notifications:', error);
    }
  }, [token]);

  useEffect(() => {
    refresh();
    if (!token) return undefined;
    const interval = setInterval(refresh, 30000);
    const handleChange = (event) => {
      const detail = event.detail || {};
      if (detail.kind === 'read' && detail.id) {
        setNotifications((current) => current.map((item) => item._id === detail.id ? { ...item, status: 'read' } : item));
        setTotalUnread((current) => Math.max(0, current - 1));
      } else if (detail.kind === 'read-all') {
        setNotifications((current) => current.map((item) => ({ ...item, status: 'read' })));
        setTotalUnread(0);
      } else if (detail.kind === 'dismiss' && detail.id) {
        setNotifications((current) => current.filter((item) => item._id !== detail.id));
        if (detail.wasUnread) setTotalUnread((current) => Math.max(0, current - 1));
      } else if (detail.kind === 'snapshot' && Array.isArray(detail.notifications)) {
        setNotifications(detail.notifications);
      } else {
        refresh();
      }
    };
    window.addEventListener(NOTIFICATION_STATE_EVENT, handleChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener(NOTIFICATION_STATE_EVENT, handleChange);
    };
  }, [token, refresh]);

  const result = useMemo(() => {
    const hrefs = hrefKey ? hrefKey.split('|') : [];
    const badges = {};
    notifications.forEach((notification) => {
      const target = targetFor(notification, portal, hrefs);
      if (!target) return;
      badges[target] ||= { unread: 0, actionRequired: 0 };
      if (notification.status === 'unread') badges[target].unread += 1;
      if (hasPendingAction(notification)) badges[target].actionRequired += 1;
    });
    return { badges };
  }, [notifications, portal, hrefKey]);

  const badgeFor = useCallback((href) => result.badges[href] || { unread: 0, actionRequired: 0 }, [result.badges]);
  const badgeForMany = useCallback((hrefs) => hrefs.reduce((sum, href) => {
    const badge = result.badges[href] || {};
    return { unread: sum.unread + (badge.unread || 0), actionRequired: sum.actionRequired + (badge.actionRequired || 0) };
  }, { unread: 0, actionRequired: 0 }), [result.badges]);

  return { ...result, totalUnread, badgeFor, badgeForMany, refresh };
}

export function NavigationNotificationBadge({ badge, dark = false }) {
  if (!badge?.unread && !badge?.actionRequired) return null;
  return (
    <span className="ml-auto flex items-center gap-1" aria-label={`${badge.unread || 0} unread, ${badge.actionRequired || 0} action required`}>
      {badge.unread > 0 && (
        <span title="Unread notifications" className="inline-flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full bg-rose-500 text-white text-[11px] font-semibold">
          {badge.unread > 99 ? '99+' : badge.unread}
        </span>
      )}
      {badge.actionRequired > 0 && (
        <span title="Action still required" className={`inline-flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full text-[11px] font-semibold ${dark ? 'bg-amber-300 text-amber-950' : 'bg-amber-100 text-amber-800 ring-1 ring-amber-300'}`}>
          {badge.actionRequired > 99 ? '99+' : badge.actionRequired}
        </span>
      )}
    </span>
  );
}
